// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import Previous from "@stoneage/XDL/Icons/ChevronLeft";
import Next from "@stoneage/XDL/Icons/ChevronRight";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            mergeCss: true,
            sizeVariant: "small",
            cssOverrides: {
                formField: css``,
                formInputWrapper: css`
                    width: auto;
                    max-width: 75px;
                    min-width: 60px;
                `,
            },
            labelProps: {
                mergeCss: true,
                transform: "capitalize",
                ...fontConfig.xSmallSingleLine,
                weight: fontConfig.weight.regular,
                color: "text",
                css: css`
                    padding: 4px 9px 4px 0;
                    min-height: initial;
                    letter-spacing: 0;
                    display: flex;
                    align-items: center;
                    width: fit-content;
                    white-space: nowrap;
                `,
            },
            css: css``,
        },
        navIconsSrc: {
            firstPage: "ChevronsLeft",
            previousPage: Previous,
            nextPage: Next,
            lastPage: "ChevronsRight",
        },
        buttonProps: {
            mergeCss: true,
            sizeVariant: "small",
            typographyProps: {
                ...fontConfig.xSmall,
                css: css`
                    letter-spacing: 0;
                `,
            },
            css: css`
                min-height: 18px;
                border: none;
                ${IconWrapper} {
                    color: ${color("common.ui7")};
                    width: 8px;
                    height: 14px;
                }
                &:disabled {
                    background: transparent;
                    ${IconWrapper} {
                        color: ${color("common.ui7", 0.33)};
                    }
                }
                &:hover:not(:disabled),
                &:active:not(:disabled) {
                    background: transparent;
                    border: none;
                    ${IconWrapper} {
                        color: ${color("primary")};
                    }
                }
                &:focus {
                    background: transparent;
                    border: none;
                    ${IconWrapper} {
                        color: ${color("common.ui1")};
                    }
                }
            `,
        },
        cssOverrides: {
            pagination: css`
                gap: 50px;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 0;
                                flex-wrap: nowrap;
                            `,
                            css`
                                flex-wrap: nowrap;
                            `,
                            null,
                            css`
                                margin: 0;
                            `,
                            null,
                        ],
                        "min",
                    )};
                margin: 0;
                ${({ justifyContent }: any) =>
                    justifyContent &&
                    css`
                        justify-content: ${justifyContent};
                    `}

                &.divider {
                    &:not(.exclude-offset-top) {
                        margin-top: 25px;
                    }
                    padding-top: 14px;
                    border-top: 1px solid ${color("common.border")};
                }
            `,
            ellipsis: css`
                margin: 0 10px;
            `,
            linkList: css`
                & > ul {
                    display: flex;
                    align-items: center;
                }
                & > .pagination-container {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    & > .pagination-element {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        & .form-input-wrapper {
                            max-width: 40px;
                        }
                        & .page-count {
                            font-size: ${fontConfig.xSmallSingleLine.size}px;
                            line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                            font-weight: ${fontConfig.weight.regular};
                            white-space: nowrap;
                            text-transform: lowercase;
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["pagination"];
